import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { capitalize } from "lodash";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { ReactNode, useState } from "react";

import {
  Deactivate10DlcCampaignMutation,
  Deactivate10DlcCampaignMutationVariables,
  GetTenDlcCampaignsForSubaccountQuery,
  ManualSyncTcrMnoMetadataMutation,
  ManualSyncTcrMnoMetadataMutationVariables,
  ProviderName,
  Remove10DlcCampaignFromSubaccountMutation,
  Remove10DlcCampaignFromSubaccountMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import ChevronDown from "~frontend/components/icons/ChevronDown";
import ChevronUp from "~frontend/components/icons/ChevronUp";
import { LIST_OWNED_PHONE_NUMBERS } from "~frontend/graphql/ownedPhoneNumber";
import {
  DEACTIVATE_10DLC_CAMPAIGN,
  LIST_10DLC_CAMPAIGNS,
  MANUAL_SYNC_TCR_MNO_METADATA,
  REMOVE_10DLC_CAMPAIGN_FROM_SUBACCOUNT,
} from "~frontend/graphql/tenDlcCampaigns";
import {
  useCurrentUserIsStaff,
  useTenant,
} from "~frontend/providers/LoggedInUserProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { PhoneNumberTable } from "./PhoneNumberTable";
import { FriendlyName } from "./TenDlc/FriendlyName";
import { NumberLimit } from "./TenDlc/NumberLimit";
import { OwnedPhoneNumberForList } from "./types";

function ManualSyncButton({
  tcrCampaignId,
  subaccountId,
}: {
  tcrCampaignId: string;
  subaccountId: string;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const [manualSyncTcrMnoMetadata, { loading }] = useMutation<
    ManualSyncTcrMnoMetadataMutation,
    ManualSyncTcrMnoMetadataMutationVariables
  >(MANUAL_SYNC_TCR_MNO_METADATA, {
    onErrorSnackbarMessage: "Failed to refresh TCR & MNO metadata",
  });

  const handleManualSync = async () => {
    if (loading) {
      return;
    }

    const { errors } = await manualSyncTcrMnoMetadata({
      variables: {
        input: {
          tcrCampaignId,
          subaccountId,
        },
      },
    });

    if (errors) {
      enqueueSnackbar(
        `Failed to refresh TCR & MNO metadata for ${tcrCampaignId}`,
        {
          variant: "error",
        }
      );
    } else {
      enqueueSnackbar(
        `Successfully refreshed TCR & MNO metadata for ${tcrCampaignId}`,
        {
          variant: "success",
        }
      );
    }
  };

  return (
    <Button
      size="small"
      onClick={handleManualSync}
      variant="outlined"
      color="secondary"
      disabled={loading}
    >
      {loading ? "Refreshing..." : "Refresh TCR & MNO Metadata"}
    </Button>
  );
}

function RemoveCampaignButton({
  tcrCampaignId,
  subaccountId,
}: {
  tcrCampaignId: string;
  subaccountId: string;
}) {
  const confirm = useConfirm();

  const [removeCampaign, { loading }] = useMutation<
    Remove10DlcCampaignFromSubaccountMutation,
    Remove10DlcCampaignFromSubaccountMutationVariables
  >(REMOVE_10DLC_CAMPAIGN_FROM_SUBACCOUNT, {
    onErrorSnackbarMessage: "Failed to remove campaign from subaccount",
    refetchQueries: [LIST_10DLC_CAMPAIGNS, LIST_OWNED_PHONE_NUMBERS],
    awaitRefetchQueries: true,
  });

  const handleRemoveCampaign = async () => {
    if (loading) {
      return;
    }

    const { confirmed } = await confirm({
      title: `Remove campaign ${tcrCampaignId} from subaccount?`,
      description: "This will archive all phone numbers associated to it.",
    });

    if (confirmed) {
      await removeCampaign({
        variables: { input: { tcrCampaignId, subaccountId } },
      });
    }
  };

  return (
    <Button
      size="small"
      variant="outlined"
      color="secondary"
      onClick={handleRemoveCampaign}
      disabled={loading}
    >
      Remove Campaign From Subaccount
    </Button>
  );
}

function DeactivateCampaignButton({
  tcrCampaignId,
}: {
  tcrCampaignId: string;
}) {
  const getErrorMessage = useErrorMessageFormatter();

  const [dialogOpen, setDialogOpen] = useState(false);

  const [deactivationError, setDeactivationError] = useState<string | null>(
    null
  );

  const [deactivateCampaign, { loading }] = useMutation<
    Deactivate10DlcCampaignMutation,
    Deactivate10DlcCampaignMutationVariables
  >(DEACTIVATE_10DLC_CAMPAIGN, {
    refetchQueries: [LIST_10DLC_CAMPAIGNS, LIST_OWNED_PHONE_NUMBERS],
    awaitRefetchQueries: true,
  });

  const handleDeactivateCampaign = async () => {
    const { errors } = await deactivateCampaign({
      variables: { input: { tcrCampaignId } },
    });

    if (errors) {
      setDeactivationError(getErrorMessage(errors));
    } else {
      setDialogOpen(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="error"
        onClick={() => setDialogOpen(true)}
        disabled={loading}
      >
        Deactivate Campaign
      </Button>
      <Dialog open={dialogOpen}>
        <DialogTitle>Deactivate Campaign {tcrCampaignId}</DialogTitle>
        <DialogContent>
          {deactivationError && (
            <Alert severity="error">{deactivationError}</Alert>
          )}
          <Typography>
            Are you sure you want to permanantly deactivate campaign{" "}
            {tcrCampaignId}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="tertiary"
            disabled={loading}
            onClick={() => setDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={loading}
            onClick={handleDeactivateCampaign}
          >
            Deactivate Campaign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export const TenDlcCampaign = ({
  campaign,
  tableParams,
}: {
  campaign: GetTenDlcCampaignsForSubaccountQuery["tenDlcCampaignsForSubaccount"][number];
  tableParams: {
    sort: string | null;
    setDefaultPhoneNumber: (
      phoneNumber: OwnedPhoneNumberForList
    ) => Promise<void>;
    providerFilter: string[] | null;
  };
}): ReactNode => {
  const tenant = useTenant()!;
  const { subaccount } = useSubaccount()!;
  const isStaff = useCurrentUserIsStaff();

  const isArchived = campaign.status === "ARCHIVED";
  const [collapsed, setCollapsed] = useState(false);

  const archivedTag = (
    <Typography
      component="span"
      sx={{
        px: 0.5,
        fontWeight: 600,
        fontSize: "10px",
        fontStyle: "normal",
        backgroundColor: palette.lightGray,
        color: palette.darkGray,
      }}
    >
      Archived
    </Typography>
  );

  return (
    <>
      <FlexBox justifyContent="space-between">
        <FlexBox direction="column" sx={{ width: "100%" }} gap={1}>
          <FlexBox justifyContent="space-between">
            <Box>
              <FriendlyName campaign={campaign} isStaff={isStaff} />
              <Typography variant="body1" component="div">
                {campaign.provider !== ProviderName.Bandwidth
                  ? `[${capitalize(campaign.provider)}] `
                  : null}
                10DLC Campaign ID: {campaign.tcrCampaignId}{" "}
                {isArchived ? archivedTag : null}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setCollapsed(!collapsed)}
              sx={{ height: "fit-content" }}
            >
              {collapsed ? (
                <ChevronDown sx={{ fontSize: "12px", strokeWidth: 2 }} />
              ) : (
                <ChevronUp sx={{ fontSize: "12px", strokeWidth: 2 }} />
              )}
            </IconButton>
          </FlexBox>

          {collapsed ? null : (
            <>
              {isStaff && campaign.provider === ProviderName.Bandwidth && (
                <NumberLimit campaign={campaign} />
              )}

              <FlexBox justifyContent="space-between" sx={{ width: "100%" }}>
                {!isArchived && (
                  <FlexBox direction="column" sx={{ maxWidth: "50%" }}>
                    <Typography variant="body3" component="div">
                      <strong>Sending limits:</strong>
                    </Typography>

                    {/* These div bodies are using string composition in order to make it easier to verify the content
                  in tests (.getByText can't find the full string if broken up by new lines) */}
                    <Typography variant="body3" component="div">
                      {`${(campaign.tpm || 0).toLocaleString()} SMS segments per minute (AT&T)`}
                    </Typography>
                    <Typography variant="body3" component="div">
                      {`${(campaign.mmsTpm || 0).toLocaleString()} MMS messages per minute (AT&T)`}
                    </Typography>
                    {campaign.brandTmobileCap !== null && (
                      <Typography variant="body3" component="div">
                        {`${(campaign.brandTmobileCap || 0).toLocaleString()} SMS segments and MMS messages per day (T-Mobile)`}
                      </Typography>
                    )}
                  </FlexBox>
                )}

                {isStaff ? (
                  <FlexBox gap={1} alignItems="flex-end">
                    <Box>
                      <ManualSyncButton
                        tcrCampaignId={campaign.tcrCampaignId}
                        subaccountId={subaccount.id}
                      />
                    </Box>
                    <Box>
                      <RemoveCampaignButton
                        tcrCampaignId={campaign.tcrCampaignId}
                        subaccountId={subaccount.id}
                      />
                    </Box>
                    {tenant.deactivatedAt && (
                      <Box>
                        <DeactivateCampaignButton
                          tcrCampaignId={campaign.tcrCampaignId}
                        />
                      </Box>
                    )}
                  </FlexBox>
                ) : (
                  <div />
                )}
              </FlexBox>

              <PhoneNumberTable
                rows={campaign.numbers || []}
                {...tableParams}
              />
            </>
          )}
        </FlexBox>
      </FlexBox>
    </>
  );
};
